/**
 * attach-target.ts —— 「附件要发给谁」的暂存
 *
 * 为什么需要这个东西：
 *   聊天页的加号菜单里点「拍照」，会把摄像头面板**压成覆盖层**推上去。
 *   但覆盖层机制只带 `{ kind, refId }` 两个字段（见 ui-store 的 OverlayEntry），
 *   没法顺手把「拍完发给哪个会话」也传过去。
 *
 * 三种可选做法：
 *   ① 扩展 OverlayEntry 加一个 payload 字段
 *      → 动的是全局机制；payload 是 unknown，会让「覆盖层只描述位置」这件事变浑。
 *   ② 把 sessionId 塞进 refId
 *      → refId 对 camera 这一层本来没用，塞进去是**滥用字段**：
 *        看代码的人会以为 camera 层的 refId 有语义，实际上没有。
 *   ③ 模块级暂存（本文件）
 *      → ✅ 采用。语义清晰：「此刻有一个待发送的附件目标」，
 *        而且它天然是**一次性**的 —— 取走就清空。
 *
 * 内存态、刷新即失效 —— 和 handles 一样，它是「此刻对话的上下文」，
 * 不是持久数据。
 */

export interface AttachTarget {
  /** 拍完（或选完）之后，附件要发到哪个会话 */
  sessionId: string;
}

let pending: AttachTarget | null = null;

/** 设置待发送目标（打开拍照面板前调用） */
export function setAttachTarget(target: AttachTarget): void {
  pending = target;
}

/**
 * 取出待发送目标并**立即清空**。
 *
 * 「取走即清空」是有意的：否则用户取消拍照后再从别处打开摄像头，
 * 会莫名其妙地把照片发到上一次那个会话里。
 */
export function takeAttachTarget(): AttachTarget | null {
  const t = pending;
  pending = null;
  return t;
}

/** 放弃待发送目标（用户取消时调用，与 take 的区别是不返回内容、语义更明确） */
export function clearAttachTarget(): void {
  pending = null;
}

/** 是否有一个待发送目标（供组件判断当前是不是「聊天页发起的拍照」） */
export function hasAttachTarget(): boolean {
  return pending !== null;
}
